import { ref, onMounted, watch, onBeforeUnmount, nextTick, computed, watchEffect, onErrorCaptured } from 'vue'
import { useForm, useField, defineRule } from 'vee-validate'
import { useStore, mapGetters } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { Render } from 'src/utils/helper/render'
import TextField from 'components/form/text-field/index.vue'
import TextEditor from 'components/form/text-editor/index.vue'
import FouSelect from 'components/form/fou-select/index.vue'
import { cloneDeep, isEmpty, isEqual } from 'lodash'
import AnimationProcessing from 'components/animations/processing'
import AddFontsSideBar from 'src/pages/bundle-submission/components/sidebar/AddFontsSideBar.vue'
import Draggable from 'vuedraggable'
import { initialPagination } from 'src/vuex/modules/bundle-submission'
import Misc from 'src/utils/helper/misc'
import { getFoundrySubTitle, findBlockedFoundryInList } from 'src/utils/helper/contractTermination'
import i18n from 'src/assets/i18n/i18n'
import AiTextEditor from 'components/form/ai-text-editor/index.vue'
import {
  AI_SUGGESTION_ASSET_TYPES,
  DESCRIPTION_CHAR_LIMITS
} from 'src/utils/constants/common'
import FouEmailVerify from 'components/general/fou-email-verify';

import PartNumber from '../components/add-fonts/components/part-number/index.vue'
import PlusIcon from '../components/icons/plus-icon/PlusIcon.vue'
import DeleteIcon from '../components/icons/delete-icon/DeleteIcon.vue'
import DraggableIcon from '../components/icons/draggable-icon/DraggableIcon.vue'
import FontPreviewer from '../components/font-previewer/FontPreviewer.vue'
import DeleteFoundryModal from '../components/delete-foundry-modal/DeleteFoundryModal.vue'
import ImageUploader from '../components/image-uploader/index.vue'
import ImageInfo from '../components/image-info/index.vue'
import BackIcon from '../components/icons/back-icon/index.vue'
import BundlePrice from '../components/bundle-price/BundlePrice.vue'
import PreviewDraft from '../components/preview-draft/PreviewDraft.vue'
import ConfirmPublishModal from '../components/confirm-publish-modal/ConfirmPublishModal.vue'
import ConfirmDiscardModal from '../components/confirm-discard-modal/ConfirmDiscardModal.vue'

export default {
  components: {
    FouEmailVerify,
    TextField,
    TextEditor,
    BackIcon,
    FouSelect,
    AnimationProcessing,
    PartNumber,
    AddFontsSideBar,
    PlusIcon,
    DeleteIcon,
    DraggableIcon,
    FontPreviewer,
    Draggable,
    DeleteFoundryModal,
    ImageUploader,
    ImageInfo,
    BundlePrice,
    PreviewDraft,
    ConfirmPublishModal,
    ConfirmDiscardModal,
    AiTextEditor
  },

  created() {
    /* feature load families ends */
    this.pagination = initialPagination
    this.families = []
    this.getFoundries()
  },

  setup() {
    const addFontsValidationMessage = i18n().global.t('pages.bundle.draft.validation_message.add_fonts')
    const $t = i18n().global.t;

    const bannedWord = ref(false)
    const descriptionWarningWords = ref([])
    const warningWord = ref(false)
    const warningWordList = ref([])
    const bannedWordForDescription = ref(false)
    const warningWordForDescription = ref(false)
    const bundleFormData = ref({
      name: '',
      foundries: [],
      description: [{ language: 'en', description: '' }],
      partNumber: '',
      collection: [],
      prices: []
    })
    const route = useRoute()
    const router = useRouter()
    const store = useStore()
    const isShowThingstoKeepModal = ref(false)
    const showThingstoKeepModal = () => {
      isShowThingstoKeepModal.value = !isShowThingstoKeepModal.value
    }

    const aiTextEditorConfig = ref({
      aiEnabled: true,
      placeholder: {
        ai_disabled: $t('pages.aiTextEditor.placeholder_bundles', { moduleName: 'bundle' }),
        ai_enabled: $t('pages.aiTextEditor.ai_enabled_placeholder', { moduleName: 'bundle' })
      },
      aiActions: {
        boostVisibility: true,
        polishWriting: true,
        fixGrammar: true,
        shortenText: true,
      },
      assetType: AI_SUGGESTION_ASSET_TYPES.bundle,
      assetId: route.params.id,
      charLimits: DESCRIPTION_CHAR_LIMITS
    })


    defineRule('custom_rule', (value, _, context) => {

      if (imageStatus.value == 0 && (!value || (value.length >= 3 && value.length <= 15))) {
        return true;
      } else if (imageStatus.value == 0) {
        return i18n().global.t('pages.bundle.draft.validation_message.bundle_images');
      }
      return true
    });
    const { errors, validateField, validate, setErrors } = useForm()
    const foundriesRules = 'required_key_values:label,value'

    const uploadImageDescriptionLength = ref(5)

    const {
      errors: foundriesErrors,
      validate: foundriesValidator,
      value: foundriesValue
    } = useField('foundries', foundriesRules, {
      initialValue: bundleFormData.value.foundries
    })

    // initial load feature

    /**
     * validate bundle fonts field before updating bundle submission. This is called when clicks on add fonts panel
     */
    async function onUpdateFonts() {
      // Since, fonts are removed from child component by calling mutation so, no need to remove fonts from this page
      if (bundleInfo.value.collection.length < 2) {
        setErrors({
          'bundle fonts': addFontsValidationMessage
        })
        // return
      }
      updateBundleSubmission()
    }

    const addFontsSideBarRef = ref(null)
    const isSideBarInitialLoaded = ref(false)
    const isBundleNameUnique = ref(true)
    const isAddFontsSideBarOpen = ref(false)
    const sessionFoundryId = computed(() => store.getters['profile/sessionFoundryId'])
    const user = computed(() => store.getters['auth/user'])
    const getLogoutState = computed(() => store.getters['auth/getLogoutState'])
    const formattedFamilies = computed(() => store.getters['bundle/formattedFamilies'])
    const bundleImages = computed(() => store.getters['bundle/getBundleInfo']?.assets?.main)
    const bundleStatus = computed(() => store.getters['bundle/getBundleStatus'])
    const loadingStatus = computed(() => store.getters['bundle/allImageUpload'])
    const pricceError = ref(false)
    const imageStatus = ref(0)

    const priceErrorUpdate = (error) => {
      pricceError.value = Object.keys(error).length !== 0
    }

    const isPublishButtonActive = ref(false)
    const savingDraft = ref(false)

    watchEffect(() => {
      if (!isAddFontsSideBarOpen.value) {
        // scroll to top
        if (addFontsSideBarRef.value?.scrollElement) {
          const el = addFontsSideBarRef.value.scrollElement
          if (el && typeof el.scrollTo === 'function') {
            el.scrollTo(0, 0)
          } else if (el) {
            el.scrollTop = 0
          }
        }
      }
    })

    watch(
      () => [bundleFormData, isBundleNameUnique, bundleImages],
      ([newBundleFormData, newIsBundleNameUnique, newBundleImages]) => {
        if (newIsBundleNameUnique.value && newBundleFormData?.value && newBundleFormData?.value.collection?.length > 1 && newBundleFormData?.value.description && newBundleFormData?.value.description[0].description !== '' && newBundleFormData?.value.name.length > 0 && newBundleFormData?.value.prices?.length > 0 && newBundleImages?.value?.length >= 3) {
          isPublishButtonActive.value = true
        } else {
          isPublishButtonActive.value = false
        }
      },
      { deep: true }
    )

    watch(
      errors,
      (newValue, oldValue) => {
        if (newValue && Object.keys(newValue).length > 0) {
          isPublishButtonActive.value = false
        }
      },
      { deep: true }
    )

    watch(
      bundleStatus,
      (newValue, oldValue) => {
        if (newValue) {
          bundleFormData.value.collection = newValue.collection
        }
      },
      { deep: true }
    )

    watch(
      loadingStatus,
      (newValue, oldValue) => {
        imageStatus.value = newValue
      }
    );

    const inspirationalImages = computed(() => store.getters['bundle/getBundleInfo']?.assets?.inspirational)

    async function validateFoundriesAndOpenSideBar() {
      isSideBarInitialLoaded.value = true
      const result = await foundriesValidator()

      if (result.valid) {
        if (addFontsSideBarRef.value.responseSearchTerm) {
          addFontsSideBarRef.value?.handleArrowBack()
        }

        isAddFontsSideBarOpen.value = true
        if (isEmpty(formattedFamilies.value)) {
          handleGetFamilies()
        }
      }
    }

    const bundleInfo = computed({
      get: () => store.state.bundle.bundleInfo,
      set: newBundleInfo => store.commit('bundle/UPDATE_BUNDLE_INFO', newBundleInfo)
    })

    const updateBannedWord = (value) => {
      bannedWord.value = value
    }

    const listOfWarningWords = (value) => {
      warningWordList.value = value
    }

    const bundleDescriptionWarningWordList = (value) => {
      descriptionWarningWords.value = value
    }

    const updateWarningWord = (value) => {
      warningWord.value = value
    }

    const updateBundleName = async () => {
      if (bannedWord.value) {
        return
      }
      const { valid } = await validateField('bundle name')
      if (valid) {
        savingDraft.value = true
        const payload = { bundleName: bundleFormData.value.name, bundleId: route.params.id }
        store
          .dispatch('bundle/checkisBundleUnique', payload)
          .then(response => {
            isBundleNameUnique.value = response
            if (response) {
              store.commit('bundle/UPDATE_BUNDLE_NAME', bundleFormData.value.name)
              updateBundleSubmission()
            }
          })
          .finally(() => {
            savingDraft.value = false
          })
      }
    }


    const isBundleLive = computed(() => isEqual(bundleStatus.value.state, IS_LIVE))

    // Api to save draft bundle submission
    function updateBundleSubmission() {
      if (isBundleLive.value) {
        savingDraft.value = false
      }
      else {
        return store
          .dispatch('bundle/updateBundle', route.params.id)
          .finally(() => {
            savingDraft.value = false
          })
          .then(response => {
            if (!isEmpty(response.bundle?.prices)) {
              updateBundlePriceModelValue(response.bundle.prices)
            }
          })
      }
    }

    /* feature load families starts */

    const pagination = computed({
      get() {
        return store.state.bundle.pagination
      },
      set(paginationData) {
        store.commit('bundle/setPagination', paginationData)
      }
    })

    const families = computed({
      get: () => store.state.bundle.families,
      set: newFamilies => store.commit('bundle/setFamilies', newFamilies)
    })

    const shouldFetchFamilies = ref(false)

    watch(
      () => bundleFormData.value.foundries,
      newValue => {
        if (isEmpty(newValue)) {
          families.value = []
          pagination.value = cloneDeep(initialPagination)
        }
      }
    )

    // Blur foundries select
    async function onFoundriesBlur() {
      const response = await foundriesValidator()

      if (response.valid && shouldFetchFamilies.value) {
        await handleGetFamilies()
        shouldFetchFamilies.value = false
      }

      return response
    }

    async function handleGetFamilies() {
      pagination.value.page = 1

      const res = await getFamiliesByFoundries()
      if (isEqual(res?.data?.meta?.nextPage, pagination.value.page)) {
        return
      }
      pagination.value.page += 1
    }

    async function getFamiliesByFoundries() {
      const joinedFoundries = bundleFormData.value.foundries.map(foundry => foundry.value || foundry.id).join(',')
      return store.dispatch('bundle/loadMoreFamilies', {
        foundryIds: joinedFoundries,
        page: pagination.value.page,
        perPage: pagination.value.perPage,
        includeCount: true
      })
    }

    /* feature load families ends */

    onMounted(() => {
      clearFoundries()
      isPreviewDraftModalOpen.value = false
      store.commit('bundle/setAddFontsValidationErrors', setErrors)
      // adding class to body to resolve the conflict between support button and cta buttons of bundle submission draft
      document.querySelector('body').classList.add('is-create-bundle-draft')
    })


    const foundriesOptions = ref([])

    const foundriesData = computed({
      get: () => store.state.bundle.foundriesData,
      set: val => store.commit('bundle/setFoundriesData', val)
    })

    const loading = ref(true)
    const UFDA2 = ref('UFDA 2')
    const bundlePermission = ref('bundles')

    /**
     * Api to get the bundle detail
     */
    const isBundleNew = ref(true)

    const foundrySelectRef = ref(null)

    watch(
      () => foundrySelectRef.value?.inputValueInSingleString,
      () => {
        clearFoundries()
      }
    )

    function onFoundriesSelected(foundries) {
      const filtered = foundries.filter(foundry => foundry && foundry.value !== 'all-options') ?? []
      const isUserAdmin = store.getters['auth/isUserAdmin']
      const blocked = findBlockedFoundryInList(filtered, f => f.value, isUserAdmin)
      if (blocked) {
        store.commit('auth/setContractTerminationModal', { show: true, props: blocked.modalProps })
        const allowed = filtered.filter(f => f.value !== blocked.item.value)
        nextTick(() => {
          bundleFormData.value.foundries = allowed
          foundriesValue.value = allowed
          if (foundrySelectRef.value) {
            foundrySelectRef.value._value = allowed
          }
        })
        return
      }
      bundleFormData.value.foundries = filtered
      // Since useField is used, to make value update and make validation work setting its internal value
      foundriesValue.value = bundleFormData.value.foundries
      clearFoundries()
      foundriesValidator().then(response => {
        savingDraft.value = true
        updateFoundries(response.valid)
        shouldFetchFamilies.value = true
        families.value = []
        pagination.value = cloneDeep(initialPagination)
      })
    }

    const existedFoundryFont = ref({})
    /**
     * Clear foundries select
     * @param {Object} foundry
     */

    function onFoundriesDeselected(foundry) {
      // check if the user is deselecting the only selected foundry.
      // i.e. when the selected foundry is only 1.
      if (isEmpty(bundleFormData.value.foundries)) {
        showErrorWhileDeselectingFoundry(foundry)
      } else {
        // if the fonts of the deselected foundry are already added to the bundle, then ask for confirmation
        existedFoundryFont.value = bundleInfo.value.collection.find(bundleFont =>
          isEqual(bundleFont.foundryId, foundry.value)
        )

        // if found foundry font
        if (!isEmpty(existedFoundryFont.value)) {
          if (!isEmpty(bundleInfo.value.foundries)) {
            bundleFormData.value.foundries = mapFoundriesDataIntoLabelValue(bundleInfo.value.foundries)
          }
          openFoundryRemoveModal(true)
          return
        }

        savingDraft.value = true
        bundleFormData.value.foundries = bundleFormData.value.foundries.filter(
          bundleFoundry => bundleFoundry.value !== foundry.value
        )

        // Since useField is used, to make value update and make validation work setting its internal value
        foundriesValue.value = bundleFormData.value.foundries
        clearFoundries()

        // if foundries removed then its related fonts should be removed from add fonts panel
        families.value = families.value.filter(family => !isEqual(family.foundryId, foundry.value))
        shouldFetchFamilies.value = true
        updateFoundries(true)
      }
    }

    const showErrorWhileDeselectingFoundry = foundry => {
      store.commit('errors/add', {
        response: {
          status: 500,
          data: {
            message: $t('pages.bundle.errorMessages.atleast_one_foundry')
          }
        }
      })
      bundleFormData.value.foundries = [foundry]
      foundriesValue.value = bundleFormData.value.foundries
    }

    const updateFoundries = valid => {
      if (valid) {
        const selectedFoundries = bundleFormData.value.foundries.map(foundry => {
          return {
            id: foundry.value,
            name: foundry.label
          }
        })
        store.commit('bundle/UPDATE_FOUNDRIES', selectedFoundries)
        updateBundleSubmission()
      }
    }

    const updateBannedWordDesc = (value) => {
      bannedWordForDescription.value = value
    }

    const updateWarningWordDesc = (value) => {
      warningWordForDescription.value = value
    }

    const handleBundleDescriptionHtmlChange = (value) => {
      bundleFormData.value.description[0].description = value
    }

    async function updateBundleDescription() {
      if (bannedWordForDescription.value) {
        return
      }

      if (bundleFormData.value.description[0].description !== '') {
        savingDraft.value = true
        store.commit('bundle/UPDATE_DESCRIPTION', bundleFormData.value.description)
        updateBundleSubmission()
      }
    }

    function clearFoundries() {
      if (isEmpty(bundleFormData.value.foundries) && !isEmpty(foundrySelectRef.value)) {
        foundrySelectRef.value.inputValueInSingleString = ''
      }
    }

    const updatePartNumber = partNumber => {
      savingDraft.value = true
      bundleFormData.value.partNumber = partNumber
      store.commit('bundle/UPDATE_PART_NUMBER', partNumber)
      updateBundleSubmission()
    }

    const goBack = () => {
      router.push({ name: 'BundleDashboard' })
    }

    const generatePreview = async () => {
      const response = await validate()
      const priceValidationResponse = await bundlePriceRef.value.validate()

      if (response.valid && priceValidationResponse.valid && isBundleNameUnique.value) {
        isPreviewDraftModalOpen.value = true
      } else {
        setValidationErrorMessage()
        isPreviewDraftModalOpen.value = false
      }
    }

    const setValidationErrorMessage = () => {
      store.commit('errors/add', {
        response: {
          status: 500,
          data: {
            message: $t('pages.bundle.errorMessages.missing_fields')
          }
        }
      })
    }

    /**
     * catch any error while previewing the bundle
     */
    onErrorCaptured((err, instance, info) => {
      if (err) {
        isPreviewDraftModalOpen.value = false
      }
    })

    const validateAndOpenConfirm = async () => {
      const response = await validate()
      const priceValidationResponse = await bundlePriceRef.value.validate()

      if (response.valid && priceValidationResponse.valid && isBundleNameUnique.value) {
        isConfirmPublishModalOpen.value = true
        isBundlePublishing.value = false
      } else {
        setValidationErrorMessage()
      }
    }

    function getFontRenderSrc({ md5, name, size = 20, color = '00000' }) {
      if (typeof md5 !== 'string') return null
      return md5.length
        ? Render.get({
          md5,
          name: encodeURI(name),
          size,
          fg: color
        })
        : null
    }

    const MIN_FONTS_COUNT = 2

    /**
     * Remove font from the list.
     *  @param {String} styleId
     */
    const removeFont = styleId => {
      if (bundleInfo.value.collection.length <= MIN_FONTS_COUNT) {
        setErrors({
          'bundle fonts': addFontsValidationMessage
        })
        store.commit('bundle/UPDATE_FONT_LIST', { styleId })
        // return
      }

      store.commit('bundle/UPDATE_FONT_LIST', { styleId })

      updateBundleSubmission()
    }

    /**
     * Drag and sort section
     */
    const updateFontSort = () => updateBundleSubmission()

    onBeforeUnmount(() => {
      store.commit('bundle/RESET_BUNDLE')
      document.querySelector('body').classList.remove('is-create-bundle-draft')
    })

    /* feature remove foundry starts */
    const isFoundryRemoveModalOpen = computed({
      get: () => store.state.bundle.isFoundryRemoveModalOpen,
      set: status => store.commit('bundle/setIsFoundryRemoveModalOpen', status)
    })

    function handleFoundryRemoveModal(status) {
      if (!status) {
        shouldFetchFamilies.value = false
      }
    }

    /**
     * Open foundry remove modal
     * @param {Boolean} status
     */

    function openFoundryRemoveModal(status) {
      isFoundryRemoveModalOpen.value = status
    }

    const isFamiliesLoading = computed({
      get: () => store.state.bundle.isFamiliesLoading,
      set: (val) => store.commit('bundle/setFamiliesLoading', val)
    })

    /**
     * Remove foundry and font
     * @param {String} foundryId
     * @param {String} skuId
     */

    async function confirmRemovingFoundryAndFont(foundryId, skuId) {
      if (isEmpty(foundryId) || isEmpty(skuId)) return

      await store.dispatch('bundle/removeFoundryAndFont', {
        foundryId,
        skuId
      })

      if (isEmpty(bundleInfo.value.foundries)) {
        bundleFormData.value.foundries = []
        foundriesValue.value = []
        await updateBundleSubmission()
        await foundriesValidator()
        openFoundryRemoveModal(false)
        return
      }

      bundleFormData.value.foundries = mapFoundriesDataIntoLabelValue(bundleInfo.value.foundries)

      savingDraft.value = true
      await updateBundleSubmission()
      openFoundryRemoveModal(false)

      // reset and call api from start
      isFamiliesLoading.value = false
      pagination.value.page = 1
      const joinedFoundries = bundleFormData.value.foundries.map(foundry => foundry.value || foundry.id).join(',')
      store.dispatch('bundle/getFamiliesByFoundries', {
        foundryIds: joinedFoundries,
        page: pagination.value.page,
        perPage: pagination.value.perPage,
        includeCount: true
      })

    }

    function mapFoundriesDataIntoLabelValue(foundries) {
      return foundries.map(foundry => {
        return {
          label: foundry.name,
          value: foundry.id
        }
      })
    }

    /* feature remove foundry ends */

    /**
     * Delete the draft bundle
     */
    const discard = async () => {
      isBundleDiscarding.value = true
      const { data: success } = await store.dispatch('bundle/deleteDraft', route.params.id)
      isBundleDiscarding.value = false
      isConfirmDiscardModalOpen.value = false
      if (success) {
        router.push({ name: 'BundleDashboard' })
      }
    }

    /**
     * Update Bundle Price
     */

    async function validateAndSubmitBundlePrice(validation) {
      const response = await validation()
      const priceValidationResponse = await bundlePriceRef.value.validate()

      if (response.valid && priceValidationResponse.valid && isBundleNameUnique.value) {
        await updateBundlePrice(bundleFormData.value.prices)
      }
    }

    async function updateBundlePrice(newPrices) {
      store.commit('bundle/UPDATE_BUNDLE_PRICES', newPrices)
      savingDraft.value = true
      await updateBundleSubmission()
    }

    function updateBundlePriceModelValue(prices) {
      if (!isEmpty(prices)) {
        //  filter out those prices whose price is not available
        bundleFormData.value.prices = prices.filter(curr => curr.price)
      }
    }

    const bundlePriceRef = ref(null)

    /**
     * feature preview draft
     *
     */

    const isPreviewDraftModalOpen = computed({
      get: () => store.state.bundle.isPreviewDraftModalOpen,
      set: status => store.commit('bundle/setIsPreviewDraftModalOpen', status)
    })

    const IS_LIVE = 'in_live'

    const DISABLED_ICON_COLOR = '#c3cad4'
    const ACTIVE_ICON_COLOR = '#1A73E8'
    const DELETE_DISABLE_COLOR = '#667488'
    const iconDisabledColor = computed(() => isBundleLive.value ? DISABLED_ICON_COLOR : ACTIVE_ICON_COLOR)

    const fontDeleteDisableColor = computed(() => isBundleLive.value ? DISABLED_ICON_COLOR : DELETE_DISABLE_COLOR)

    const bundlePreviewData = computed(() => {
      return {
        ...bundleStatus.value,
        ...bundleFormData.value,
        assets: {
          main: bundleImages.value,
          inspirational: inspirationalImages.value
        },
        collection: bundleStatus.value.collection
      }
    })

    /**
     * feature confirm publish modal
     */
    const isConfirmPublishModalOpen = computed({
      get: () => store.state.bundle.isConfirmPublishModalOpen,
      set: val => store.commit('bundle/setConfirmPublishModalOpen', val)
    })

    const isBundlePublishing = computed({
      get: () => store.state.bundle.isBundlePublishing,
      set: val => store.commit('bundle/setBundlePublishing', val)
    })

    function handleClickPublish() {
      isConfirmPublishModalOpen.value = true
      isBundlePublishing.value = false
    }

    const shouldNavigateToBundleSuccess = computed({
      get: () => store.state.bundle.shouldNavigateToBundleSuccess,
      set: (val) => store.commit('bundle/setShouldNavigateToBundleSuccess', val)
    })

    async function publishBundle(bundleId) {
      if (!bundleId) return Promise.reject(new Error('Bundle id is required'))

      const response = await validate()
      const priceValidationResponse = await bundlePriceRef.value.validate()

      if (response.valid && priceValidationResponse.valid && isBundleNameUnique.value) {
        if (bundleStatus.value.state === 'in_work') {
          bundleInfo.value.state = 'in_submission'
        }
        return store.dispatch('bundle/publishBundle', bundleId).then(res => {
          shouldNavigateToBundleSuccess.value = true
          router.replace({ name: 'BundleSuccess' })
        }).catch(() => {
          if (bundleInfo.value.state === 'in_submission') {
            bundleInfo.value.state = 'in_work'
          }
        })
      } else {
        setValidationErrorMessage()
        shouldNavigateToBundleSuccess.value = false
        return Promise.reject(new Error('Validation failed'))
      }
    }

    /**
     * feature confirm discard modal
     */
    const isConfirmDiscardModalOpen = computed({
      get: () => store.state.bundle.isConfirmDiscardModalOpen,
      set: val => store.commit('bundle/setConfirmDiscardModalOpen', val)
    })

    const isBundleDiscarding = computed({
      get: () => store.state.bundle.isBundleDiscarding,
      set: val => store.commit('bundle/setBundleDiscarding', val)
    })
    const getImpersonateUser = computed(() => {
      if (user.value?.impersonate) {
        return user.value?.impersonate
      }
      const fetchFSValue = JSON.parse(window.localStorage.getItem('FS-Impersonate'))
      return fetchFSValue && !getLogoutState.value
    })
    return {
      user,
      getLogoutState,
      store,
      UFDA2,
      foundriesOptions,
      isBundleNew,
      loading,
      route,
      router,
      foundrySelectRef,
      bundleFormData,
      foundriesValue,
      foundriesErrors,
      addFontsSideBarRef,
      isSideBarInitialLoaded,
      isAddFontsSideBarOpen,
      formattedFamilies,
      bundleImages,
      isBundleNameUnique,
      savingDraft,
      bundleInfo,
      pagination,
      errors,
      uploadImageDescriptionLength,
      isFoundryRemoveModalOpen,
      foundriesData,
      bundlePermission,
      existedFoundryFont,
      inspirationalImages,
      families,
      shouldFetchFamilies,
      isPreviewDraftModalOpen,
      bundleStatus,
      bundlePriceRef,
      isBundlePublishing,
      isConfirmPublishModalOpen,
      sessionFoundryId,
      validateFoundriesAndOpenSideBar,
      validateField,
      validate,
      nextTick,
      foundriesValidator,
      removeFont,
      getFontRenderSrc,
      validateAndOpenConfirm,
      generatePreview,
      goBack,
      updatePartNumber,
      updateBundleDescription,
      onFoundriesDeselected,
      onFoundriesSelected,
      onFoundriesBlur,
      updateBundleName,
      updateBundleSubmission,
      confirmRemovingFoundryAndFont,
      handleFoundryRemoveModal,
      updateFontSort,
      handleGetFamilies,
      validateAndSubmitBundlePrice,
      isEmpty,
      getFamiliesByFoundries,
      mapFoundriesDataIntoLabelValue,
      openFoundryRemoveModal,
      updateBundlePriceModelValue,
      onUpdateFonts,
      isBundleLive,
      iconDisabledColor,
      publishBundle,
      discard,
      handleClickPublish,
      updateBundlePrice,
      shouldNavigateToBundleSuccess,
      isConfirmDiscardModalOpen,
      isBundleDiscarding,
      fontDeleteDisableColor,
      bundlePreviewData,
      getImpersonateUser,
      loadingStatus,
      imageStatus,
      isPublishButtonActive,
      priceErrorUpdate,
      pricceError,
      isShowThingstoKeepModal,
      showThingstoKeepModal,
      bannedWord,
      warningWord,
      updateBannedWord,
      listOfWarningWords,
      bundleDescriptionWarningWordList,
      warningWordList,
      descriptionWarningWords,
      updateWarningWord,
      updateBannedWordDesc,
      handleBundleDescriptionHtmlChange,
      updateWarningWordDesc,
      bannedWordForDescription,
      warningWordForDescription,
      aiTextEditorConfig
    }
  },

  methods: {
    ...Misc,
    async getFoundries() {
      const foundries = await this.store.dispatch('profile/getFoundries')
      const ufda2SignedFoundries = foundries.foundries.filter(foundry =>
        isEqual(foundry['contractSummary'], this.UFDA2)
      )

      const foundriesWithBundlePermission = ufda2SignedFoundries.filter(foundry =>
        foundry.permissions.find(permission => isEqual(permission.name, this.bundlePermission))
      )

      this.foundriesData = ufda2SignedFoundries

      this.foundriesOptions = foundriesWithBundlePermission.map(foundry => ({ label: foundry.name, value: foundry.id, subTitle: getFoundrySubTitle(foundry.contractStatus) }))

      this.getBundleDetail()
    },

    async getBundleDetail() {
      const bundleDetail = await this.store.dispatch('bundle/getBundleInfo', this.route.params.id).catch((err) => {
        return this.$router.push({ name: 'BundleDashboard' })
      })
      this.isBundleNew = isEqual(bundleDetail?.state, 'new')
      this.loading = false
      if (!this.isBundleNew) {
        this.updateBundleData(bundleDetail)
      } else {
        this.updateFoundriesWithSessionFoundry(bundleDetail)
      }
    },

    /**
     * Update the default foundry selected to be session foundry.
     * @param {Object} bundleDetail
     */
    updateFoundriesWithSessionFoundry(bundleDetail) {
      let foundries = []
      foundries = bundleDetail.foundries.map(foundry => {
        return {
          label: foundry.name,
          value: foundry.id
        }
      })
      this.nextTick(() => {
        if (this.foundrySelectRef) {
          this.foundrySelectRef._value = foundries
        }
      })
      this.bundleFormData.foundries = foundries
      if (!isEmpty(bundleDetail.foundries)) {
        this.foundriesValue = this.bundleFormData.foundries
      }
      this.handleGetFamilies()
    },

    /**
     *  Pre populate the bundle form for edit.
     * @param {Object} bundleDetail
     */
    updateBundleData(bundleDetail) {
      if (!bundleDetail) return;
      const { name, description = [{ description: '' }], partNumber, collection = [], prices = [] } = bundleDetail
      let foundries = []
      if (!isEmpty(bundleDetail.foundries)) {
        foundries = bundleDetail.foundries.map(foundry => {
          return {
            label: foundry.name,
            value: foundry.id
          }
        })
      }
      this.nextTick(() => {
        if (this.foundrySelectRef) {
          this.foundrySelectRef._value = foundries
        }
      })
      this.bundleFormData = { name, description, partNumber, foundries, collection, prices }
      if (!isEmpty(bundleDetail.foundries)) {
        this.foundriesValue = this.bundleFormData.foundries
      }
    }
  },

  computed: {
    ...mapGetters('bundle', ['getBundleMainImageScanStatus', 'getBundleInspirationImageScanStatus', 'getBundleMainImagesWithFalseAvCheck', 'getBundleInspirationImagesWithFalseAvCheck']),
  },
}
